import { useEffect, useState, useCallback } from "react";
import { Check, X, Ban, RefreshCw, ShieldAlert, Users, MessageSquare, Trash2, Loader2, AlertTriangle, MessageCircle, Search, Calendar, Gift, Radio } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import AdminGiveaways from "./AdminGiveaways";
import AdminLiveSessions from "./AdminLiveSessions";
import { ChannelResetDialog } from "../components/ChannelResetDialog";

const TABS = [
  { id: "today", label: "Heute", icon: Calendar },
  { id: "pending", label: "Freischaltungen", icon: ShieldAlert },
  { id: "members", label: "Mitglieder", icon: Users },
  { id: "messages", label: "Nachrichten", icon: MessageSquare },
  { id: "blocked", label: "Blockiert", icon: AlertTriangle },
  { id: "giveaways", label: "Verlosung", icon: Gift },
  { id: "live", label: "Live Talks", icon: Radio },
  { id: "danger", label: "Daten", icon: Trash2 },
];

const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "approved", label: "Freigeschaltet" },
  { id: "pending", label: "Wartet" },
  { id: "banned", label: "Gesperrt" },
  { id: "warned", label: "Verwarnt" },
];

export default function Admin() {
  const [tab, setTab] = useState("today");
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [today, setToday] = useState([]);
  const [members, setMembers] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [msgChannel, setMsgChannel] = useState("");

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) { console.error(err); }
  }, []);

  const loadToday = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/today${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setToday(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadPending = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/pending${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setPending(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadMembers = useCallback(async (q = "", st = "all") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (st && st !== "all") params.set("status", st);
      const { data } = await api.get(`/admin/users${params.toString() ? "?" + params : ""}`);
      setMembers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadBlocked = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blocked-messages");
      setBlocked(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadMessages = useCallback(async (q = "", ch = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (ch) params.set("channel", ch);
      const { data } = await api.get(`/admin/messages${params.toString() ? "?" + params : ""}`);
      setMessages(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Re-fetch when tab/search/filter changes
  useEffect(() => {
    if (tab === "today") loadToday(search);
    if (tab === "pending") loadPending(search);
    if (tab === "members") loadMembers(search, filter);
    if (tab === "blocked") loadBlocked();
    if (tab === "messages") loadMessages(search, msgChannel);
  }, [tab, search, filter, msgChannel, loadToday, loadPending, loadMembers, loadBlocked, loadMessages]);

  const approve = async (id) => {
    try { await api.post(`/admin/users/${id}/approve`); toast.success("Freigeschaltet ✅"); loadPending(search); loadToday(search); loadStats(); }
    catch { toast.error("Fehler"); }
  };
  const reject = async (id) => {
    if (!window.confirm("Diesen Benutzer wirklich löschen?")) return;
    try { await api.post(`/admin/users/${id}/reject`); toast.success("Gelöscht"); loadPending(search); loadMembers(search, filter); loadToday(search); loadStats(); }
    catch { toast.error("Fehler"); }
  };
  const ban = async (id) => {
    try { await api.post(`/admin/users/${id}/ban`); toast.success("Gesperrt"); loadMembers(search, filter); loadStats(); }
    catch { toast.error("Fehler"); }
  };
  const unban = async (id) => {
    try { await api.post(`/admin/users/${id}/unban`); toast.success("Entsperrt"); loadMembers(search, filter); loadStats(); }
    catch { toast.error("Fehler"); }
  };
  const resetWarnings = async (id) => {
    try { await api.post(`/admin/users/${id}/reset-warnings`); toast.success("Verwarnungen zurückgesetzt"); loadMembers(search, filter); }
    catch { toast.error("Fehler"); }
  };
  const deleteMessage = async (id) => {
    if (!window.confirm("Nachricht löschen?")) return;
    try { await api.delete(`/admin/messages/${id}`); toast.success("Nachricht gelöscht"); loadMessages(search, msgChannel); loadStats(); }
    catch { toast.error("Fehler"); }
  };
  const deleteBlocked = async (id) => {
    try { await api.delete(`/admin/blocked-messages/${id}`); toast.success("Eintrag gelöscht"); loadBlocked(); loadStats(); }
    catch { toast.error("Fehler"); }
  };
  const cleanupRecent = async () => {
    if (!window.confirm("Alle Community- und Privat-Nachrichten der letzten 7 Tage unwiderruflich löschen?")) return;
    if (!window.confirm("Letzte Warnung – wirklich alle Chats der letzten 7 Tage löschen?")) return;
    try {
      const { data } = await api.delete("/admin/messages/recent/cleanup?days=7&include_private=true");
      toast.success(`${data.community_deleted} Community- + ${data.private_deleted} Privat-Nachrichten gelöscht`);
      loadMessages(search, msgChannel);
      loadStats();
    } catch { toast.error("Fehler beim Löschen"); }
  };
  const resetAllMembers = async () => {
    if (!window.confirm("ALLE Mitglieder (außer Admin) und ALLE Nachrichten unwiderruflich löschen?")) return;
    if (!window.confirm("Bist du wirklich sicher?")) return;
    try { const { data } = await api.post("/admin/reset-members"); toast.success(`${data.deleted} Mitglieder gelöscht`); loadMembers(); loadStats(); }
    catch { toast.error("Fehler"); }
  };

  const showSearch = tab === "today" || tab === "pending" || tab === "members" || tab === "messages";
  const showFilter = tab === "members";
  const showChannelFilter = tab === "messages";

  return (
    <div data-testid="admin-page" className="space-y-4 sm:space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">Verwaltung</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter">
            Admin-Dashboard 🛡️
          </h1>
        </div>
        <button
          onClick={() => { loadStats(); if (tab === "pending") loadPending(search); if (tab === "members") loadMembers(search, filter); if (tab === "blocked") loadBlocked(); if (tab === "messages") loadMessages(search, msgChannel); }}
          data-testid="refresh-btn"
          className="text-sm bg-slate-100/70 hover:bg-slate-100 text-slate-900 px-4 py-2 rounded-full flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Aktualisieren
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
        <StatBox label="Gesamt" value={stats?.total_users} icon={Users} accent="text-[#4DBFFF]" testId="stat-total" />
        <StatBox label="Wartet" value={stats?.pending_users} icon={ShieldAlert} accent="text-amber-400" testId="stat-pending" />
        <StatBox label="Gesperrt" value={stats?.banned_users} icon={Ban} accent="text-red-400" testId="stat-banned" />
        <StatBox label="Posts" value={stats?.messages} icon={MessageSquare} accent="text-emerald-400" testId="stat-messages" />
        <StatBox label="Blockiert" value={stats?.blocked_messages} icon={AlertTriangle} accent="text-orange-400" testId="stat-blocked" />
        <StatBox label="Privat" value={stats?.private_messages} icon={MessageCircle} accent="text-purple-400" testId="stat-private" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSearch(""); }}
              data-testid={`admin-tab-${t.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${
                isActive
                  ? "bg-[#1D5BFF] text-white shadow-[0_0_24px_rgba(0,102,255,0.45)]"
                  : "bg-[#FFFFFF] text-slate-700 border border-slate-200 hover:text-slate-900"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Search + filter row */}
      {showSearch && (
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "messages" ? "Nachricht durchsuchen…" : "Name, E-Mail oder Standort…"}
              data-testid="admin-search-input"
              className="w-full bg-[#FFFFFF] border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/30"
            />
          </div>
          {showFilter && (
            <div className="flex gap-1 overflow-x-auto" data-testid="admin-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`filter-${f.id}`}
                  className={`shrink-0 px-3 py-2 rounded-full text-xs font-semibold transition-all ${
                    filter === f.id ? "bg-[#1D5BFF] text-white" : "bg-[#FFFFFF] text-slate-700 border border-slate-200 hover:text-slate-900"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {showChannelFilter && (
            <select
              value={msgChannel}
              onChange={(e) => setMsgChannel(e.target.value)}
              data-testid="admin-channel-filter"
              className="bg-[#FFFFFF] border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-[#1D5BFF]"
            >
              <option value="">Alle Kanäle</option>
              <option value="allgemein">#Allgemein</option>
              <option value="routen">#Routen</option>
              <option value="probleme">#Probleme</option>
              <option value="marktplatz">#Marktplatz</option>
              <option value="smalltalk">#Smalltalk</option>
            </select>
          )}
          {tab === "messages" && (
            <button
              onClick={cleanupRecent}
              data-testid="cleanup-last-7-btn"
              title="Löscht alle Community- und DM-Nachrichten der letzten 7 Tage"
              className="shrink-0 inline-flex items-center gap-2 bg-rose-50 hover:bg-rose-100 border border-rose-300 text-rose-700 hover:text-rose-900 text-xs font-bold px-3 py-2 rounded-full transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" /> 7-Tage-Chats löschen
            </button>
          )}
          {tab === "messages" && (
            <ChannelResetDialog onDone={() => { loadMessages(search, msgChannel); loadStats(); }} />
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade…
        </div>
      )}

      {/* Today */}
      {tab === "today" && !loading && (
        <div className="space-y-3" data-testid="today-list">
          <div className="text-xs text-slate-500">Heute registriert: {today.length}</div>
          {today.length === 0 ? (
            <Empty text="Heute hat sich noch niemand registriert." />
          ) : (
            today.map((u) => (
              <UserRow key={u.id} user={u}>
                {!u.is_approved && u.role !== "admin" && (
                  <button onClick={() => approve(u.id)} data-testid={`t-approve-${u.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" /> Freischalten
                  </button>
                )}
                {u.role !== "admin" && (
                  <button onClick={() => reject(u.id)} data-testid={`t-reject-${u.id}`} className="bg-red-500/80 hover:bg-red-500 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5">
                    <X className="w-3.5 h-3.5" /> Ablehnen
                  </button>
                )}
              </UserRow>
            ))
          )}
        </div>
      )}

      {/* Pending */}
      {tab === "pending" && !loading && (
        <div className="space-y-3" data-testid="pending-list">
          <div className="text-xs text-slate-500">Treffer: {pending.length}</div>
          {pending.length === 0 ? (
            <Empty text={search ? "Keine Treffer für die Suche." : "Keine offenen Freischaltungen."} />
          ) : (
            pending.map((u) => (
              <UserRow key={u.id} user={u}>
                <button onClick={() => approve(u.id)} data-testid={`approve-${u.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" /> Freischalten
                </button>
                <button onClick={() => reject(u.id)} data-testid={`reject-${u.id}`} className="bg-red-500 hover:bg-red-600 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5">
                  <X className="w-3.5 h-3.5" /> Ablehnen
                </button>
              </UserRow>
            ))
          )}
        </div>
      )}

      {/* Members */}
      {tab === "members" && !loading && (
        <div className="space-y-3" data-testid="members-list">
          <div className="text-xs text-slate-500">Treffer: {members.length}</div>
          {members.length === 0 ? (
            <Empty text={search || filter !== "all" ? "Keine Treffer." : "Noch keine Mitglieder."} />
          ) : (
            members.map((u) => (
              <UserRow key={u.id} user={u}>
                {u.role !== "admin" && (
                  <>
                    {!u.is_approved && (
                      <button onClick={() => approve(u.id)} data-testid={`m-approve-${u.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full">
                        Freischalten
                      </button>
                    )}
                    {u.warnings > 0 && (
                      <button onClick={() => resetWarnings(u.id)} data-testid={`reset-warn-${u.id}`} className="bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full">
                        Verwarn. reset
                      </button>
                    )}
                    {u.banned ? (
                      <button onClick={() => unban(u.id)} data-testid={`unban-${u.id}`} className="bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full">
                        Entsperren
                      </button>
                    ) : (
                      <button onClick={() => ban(u.id)} data-testid={`ban-${u.id}`} className="bg-red-500/80 hover:bg-red-500 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full">
                        Sperren
                      </button>
                    )}
                    <button onClick={() => reject(u.id)} data-testid={`del-${u.id}`} className="bg-slate-100/70 hover:bg-red-500/30 text-red-300 text-xs font-semibold px-3 py-2 rounded-full">
                      Löschen
                    </button>
                  </>
                )}
              </UserRow>
            ))
          )}
        </div>
      )}

      {/* Messages */}
      {tab === "messages" && !loading && (
        <div className="space-y-3" data-testid="messages-list">
          <div className="text-xs text-slate-500">Treffer: {messages.length}</div>
          {messages.length === 0 ? (
            <Empty text={search || msgChannel ? "Keine Treffer." : "Noch keine Nachrichten."} />
          ) : (
            messages.map((m) => (
              <div key={m.id} className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-4 flex gap-3 items-start" data-testid={`admin-msg-${m.id}`}>
                <img src={fileUrl(m.user_avatar)} alt={m.user_name} className="w-10 h-10 rounded-full object-cover border border-slate-200 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-semibold text-slate-900">{m.user_name}</span>
                    <span className="text-slate-500">in #{m.channel}</span>
                    <span className="text-slate-600">· {new Date(m.created_at).toLocaleString("de-DE")}</span>
                  </div>
                  {m.image_url && (
                    <img src={fileUrl(m.image_url)} alt="" className="mt-2 max-h-40 rounded-lg border border-slate-200" />
                  )}
                  <div className="text-sm text-slate-800 mt-1.5 break-words">{m.content}</div>
                </div>
                <button
                  onClick={() => deleteMessage(m.id)}
                  data-testid={`delete-msg-${m.id}`}
                  className="bg-red-500/15 hover:bg-red-500 hover:text-slate-900 text-red-300 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5 shrink-0"
                  title="Nachricht löschen"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Löschen
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Blocked messages */}
      {tab === "blocked" && !loading && (
        <div className="space-y-3" data-testid="blocked-list">
          {blocked.length === 0 ? (
            <Empty text="Keine blockierten Nachrichten." />
          ) : (
            blocked.map((b) => (
              <div key={b.id} className="bg-[#FFFFFF] border border-red-500/20 rounded-2xl p-4">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-900">{b.user_name}</div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-slate-500">
                      {b.channel?.startsWith("private:") ? "PRIVAT" : `#${b.channel}`} · {new Date(b.created_at).toLocaleString("de-DE")}
                    </div>
                    <button
                      onClick={() => deleteBlocked(b.id)}
                      data-testid={`delete-blocked-${b.id}`}
                      className="bg-red-500/15 hover:bg-red-500 hover:text-slate-900 text-red-300 text-xs font-semibold px-2 py-1 rounded-full"
                      title="Log-Eintrag löschen"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-slate-700 bg-[#F5F8FD] border border-slate-200/60 rounded-lg p-3 mb-2 italic break-words">
                  „{b.content}"
                </div>
                <div className="text-xs text-red-300 flex items-center gap-2 flex-wrap">
                  <ShieldAlert className="w-3.5 h-3.5" /> {b.reason}
                  {b.warning_no && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">Verwarnung {b.warning_no}/3</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Danger zone */}
      {tab === "giveaways" && !loading && (
        <AdminGiveaways />
      )}

      {tab === "live" && !loading && (
        <AdminLiveSessions />
      )}

      {/* Danger zone */}
      {tab === "danger" && (
        <div className="space-y-4" data-testid="danger-zone">
          <div className="bg-[#FFFFFF] border border-emerald-500/30 rounded-2xl p-6 space-y-3">
            <h2 className="font-display text-xl font-bold text-slate-900">Demo-Daten (Support-Team + Beispiel-Profile)</h2>
            <p className="text-sm text-slate-600">
              Legt <strong>6 TSU-Support-Mitarbeiter</strong> und <strong>12 Demo-Community-Profile</strong> (DE/AT/CH) an,
              plus eine natürliche Beispiel-Konversation im Chat.
              Idempotent – mehrmals klicken ist ungefährlich. Alle Demo-Nachrichten sind als „Demo"/„TSU Support" gekennzeichnet.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/seed-demo");
                    toast.success(`Angelegt: ${data.users.created_support} Support · ${data.users.created_demo} Demo · ${data.messages.inserted ?? 0} Nachrichten`);
                  } catch (err) {
                    toast.error("Fehler beim Seeding");
                  }
                }}
                data-testid="seed-demo-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-semibold px-5 py-2.5 rounded-full flex items-center gap-2"
              >
                <Users className="w-4 h-4" /> Support + Demo-Profile anlegen
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Alle Demo-Beispielnachrichten löschen? Die Profile bleiben erhalten.")) return;
                  try {
                    const { data } = await api.delete("/admin/seed-demo/messages");
                    toast.success(`${data.deleted} Demo-Nachrichten entfernt`);
                  } catch (err) {
                    toast.error("Fehler");
                  }
                }}
                data-testid="clear-demo-msgs-btn"
                className="bg-slate-100/70 hover:bg-slate-100 text-slate-700 font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 border border-slate-200"
              >
                <Trash2 className="w-4 h-4" /> Nur Demo-Nachrichten löschen
              </button>
            </div>
          </div>

          <div className="bg-[#FFFFFF] border border-red-500/30 rounded-2xl p-6 space-y-4">
            <h2 className="font-display text-xl font-bold text-slate-900">Daten zurücksetzen</h2>
            <p className="text-sm text-slate-600">
              Achtung – diese Aktion löscht alle Mitglieder (außer Admin), alle Chat-Nachrichten,
              alle privaten Nachrichten, alle Freundschaften und alle blockierten Nachrichten unwiderruflich.
            </p>
            <button
              onClick={resetAllMembers}
              data-testid="reset-members-btn"
              className="bg-red-500 hover:bg-red-600 text-slate-900 font-semibold px-5 py-3 rounded-full flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Mitglieder auf 0 zurücksetzen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, icon: Icon, accent, testId }) {
  return (
    <div className="bg-[#FFFFFF] border border-slate-200 rounded-xl sm:rounded-2xl p-2.5 sm:p-4" data-testid={testId}>
      <div className="flex items-center gap-1 sm:gap-2 text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-500">
        <Icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${accent}`} /> {label}
      </div>
      <div className="font-display text-lg sm:text-2xl font-black text-slate-900 mt-0.5 sm:mt-1">{value ?? "–"}</div>
    </div>
  );
}

function UserRow({ user, children }) {
  return (
    <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 flex-wrap" data-testid={`user-row-${user.id}`}>
      <img src={fileUrl(user.avatar_url)} alt={user.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border border-slate-200 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="font-semibold text-slate-900 text-sm sm:text-base truncate">{user.name}</div>
          {user.role === "admin" && <Tag color="blue">Admin</Tag>}
          {user.banned && <Tag color="red">Gesperrt</Tag>}
          {!user.is_approved && user.role !== "admin" && <Tag color="amber">Wartet</Tag>}
          {user.warnings > 0 && <Tag color="orange">{user.warnings}/3</Tag>}
        </div>
        <div className="text-[11px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1 truncate">
          {user.email} · {user.location || "—"}
        </div>
      </div>
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap w-full sm:w-auto">{children}</div>
    </div>
  );
}

function Tag({ color, children }) {
  const map = {
    blue: "bg-[#1D5BFF]/20 text-[#4DBFFF]",
    red: "bg-red-500/20 text-red-300",
    amber: "bg-amber-500/20 text-amber-300",
    orange: "bg-orange-500/20 text-orange-300",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${map[color]}`}>{children}</span>;
}

function Empty({ text }) {
  return (
    <div className="bg-[#FFFFFF] border border-slate-200/60 rounded-2xl p-10 text-center text-slate-500 text-sm">
      {text}
    </div>
  );
}
